import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { JobStatusDot } from "../components/StatusBadge";
import { UsageMeter, usePlanStatus } from "../components/PlanGate";
import { Briefcase, FileText, Clock, CheckCircle2, CalendarCheck, AlertTriangle, Plus, ChevronRight, Zap } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const STAT_DEF = [
  { key: "active_jobs", label: "Active Jobs", icon: Briefcase },
  { key: "open_permits", label: "Open Permits", icon: FileText },
  { key: "pending_review", label: "Pending Review", icon: Clock },
  { key: "approved_this_week", label: "Approved This Week", icon: CheckCircle2 },
  { key: "upcoming_inspections", label: "Inspections (7d)", icon: CalendarCheck },
  { key: "plan_objections", label: "Plan Objections", icon: FileText, danger: true },
  { key: "actionable_violations", label: "Open Violations", icon: AlertTriangle, danger: true },
  { key: "ecb_balance_due", label: "ECB Balances", icon: AlertTriangle, danger: true },
  { key: "flagged", label: "Flagged / Delayed", icon: AlertTriangle, danger: true },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const userName = location.state?.user?.name;
  const { status: planStatus } = usePlanStatus();

  useEffect(() => {
    const load = async () => {
      try {
        const [s, j] = await Promise.all([api.get("/dashboard/summary"), api.get("/jobs")]);
        setSummary(s.data);
        setJobs(Array.isArray(j.data) ? j.data : []);
      } catch (e) {
        console.error("Dashboard load failed:", e);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div className="px-4 sm:px-8 py-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <div>
          <span className="label-eyebrow">Operations Overview</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            {userName ? `Welcome back, ${userName.split(" ")[0]}.` : "Dashboard"}
          </h1>
          <p className="text-zinc-600 text-sm mt-1">Your full week — at a glance.</p>
        </div>
        <button
          onClick={() => navigate("/jobs/new")}
          data-testid="dashboard-add-job-button"
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-800"
        >
          <Plus size={16} /> Add Job
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-10">
        {STAT_DEF.map(({ key, label, icon: Icon, danger }) => (
          <div
            key={key}
            className={`border border-zinc-200 bg-white p-4 rounded-sm ${danger && summary?.[key] > 0 ? "border-red-200 bg-red-50" : ""}`}
            data-testid={`stat-${key}`}
          >
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">{label}</span>
              <Icon size={14} strokeWidth={1.5} className="text-zinc-400" />
            </div>
            <div className="font-display text-3xl font-bold tracking-tight mt-2">
              {loading ? <Skeleton className="h-8 w-10 mt-1" /> : summary?.[key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Plan limits bar — shown for free/starter users */}
      {planStatus && !planStatus.is_admin && planStatus.limits?.max_jobs < 999 && (
        <div className="border border-zinc-200 bg-white rounded-sm px-5 py-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">
              {planStatus.plan === "free" ? "Free Trial Limits" : "Plan Usage"}
            </span>
            <a href="/account?tab=billing" className="text-[11px] font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1">
              <Zap size={11} /> Upgrade
            </a>
          </div>
          <UsageMeter
            current={planStatus.usage?.jobs ?? jobs.length}
            max={planStatus.limits?.max_jobs}
            label={"Jobs used (max " + planStatus.limits?.max_jobs + ")"}
          />
          <p className="text-[11px] text-zinc-400 mt-2">
            Each job can track up to <strong>{planStatus.limits?.max_permits_per_job}</strong> permits.
            {planStatus.plan === "free" && planStatus.trial?.in_trial && (
              <span> Trial ends in <strong>{planStatus.trial.days_left}</strong> day{planStatus.trial.days_left !== 1 ? "s" : ""}.</span>
            )}
          </p>
        </div>
      )}

      {/* Jobs */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-bold tracking-tight">Active Jobs</h2>
        <span className="text-xs text-zinc-500">{jobs.length} job{jobs.length === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="border border-zinc-200 bg-white p-4 rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <Skeleton className="h-2.5 w-2.5 rounded-full" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-5 w-3/4 mt-3" />
              <Skeleton className="h-4 w-1/2 mt-2" />
              <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-zinc-100">
                <div><Skeleton className="h-3 w-10 mb-1" /><Skeleton className="h-4 w-6" /></div>
                <div><Skeleton className="h-3 w-20 mb-1" /><Skeleton className="h-4 w-16" /></div>
              </div>
            </div>
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState onClick={() => navigate("/jobs/new")} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="jobs-grid">
          {jobs.map(job => (
            <button
              key={job.job_id}
              onClick={() => navigate(`/jobs/${job.job_id}`)}
              data-testid={`job-card-${job.job_id}`}
              className="text-left border border-zinc-200 bg-white p-4 rounded-sm hover:border-zinc-400 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <JobStatusDot color={job.status_color} />
                  <span className="label-eyebrow">{job.borough}</span>
                </div>
                <ChevronRight size={16} className="text-zinc-300 group-hover:text-zinc-700 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="font-display font-bold text-base tracking-tight mt-3 line-clamp-1">{job.nickname}</div>
              <div className="text-sm text-zinc-600 mt-0.5 line-clamp-1">{job.address}</div>
              <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-zinc-100">
                <div>
                  <div className="label-eyebrow">Permits</div>
                  <div className="text-sm font-semibold mt-0.5">{job.permit_count}</div>
                </div>
                <div>
                  <div className="label-eyebrow">Next Inspection</div>
                  <div className="text-sm font-semibold mt-0.5">{fmtDate(job.next_inspection_at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const EmptyState = ({ onClick }) => (
  <div className="border border-zinc-200 bg-white p-10 rounded-sm text-center" data-testid="empty-jobs">
    <div className="w-12 h-12 mx-auto bg-zinc-100 rounded-sm flex items-center justify-center">
      <Briefcase size={20} className="text-zinc-500" />
    </div>
    <h3 className="font-display text-lg font-bold mt-4 tracking-tight">No jobs yet</h3>
    <p className="text-sm text-zinc-600 mt-1">Add your first job to start tracking permits and inspections.</p>
    <button
      onClick={onClick}
      data-testid="empty-state-add-job"
      className="mt-5 inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-800"
    >
      <Plus size={16} /> Add Your First Job
    </button>
  </div>
);
